import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Text,
  Input,
  Textarea,
  Checkbox,
  Button,
  FormControl,
  FormLabel,
  FormErrorMessage,
  Link,
  VStack,
  useColorMode,
} from '@chakra-ui/react';
import ThemePhoto from './ThemePhoto';
import { API_URL } from '../api';
import {
  CHRISTMAS_SECTION_ID,
  CHRISTMAS_TITLE,
  CHRISTMAS_SUBTITLE,
  CHRISTMAS_DATES_SHORT,
  CHRISTMAS_INTRO,
  formatCzk,
  pragueToday,
} from '../data/christmas';
// Pole a tlačítko formuláře mají stejný vzhled jako kontakt (i v tmavém režimu)
import './Contact.css';
import './ChristmasBooking.css';

// Texty sekce se mění v src/data/christmas.ts, termíny, záloha a podmínky
// rezervace na backendu (src/config/reservations.ts v hrachovenachova-be).

// Fotka bez přípony – ve světlém režimu barevná, v tmavém černobílá.
// Velká verze má 1333 px, náhled 800 px. Výběr (sizes) je schválně:
//  • telefon (≤ 480 px): fotka je přes šířku, ale uvádíme jen 266 px, aby
//    náhled stačil i na hustotě 3× (iPhone by jinak stáhl velkou barevnou
//    i černobílou verzi, ~700 kB). Vyjde to na ~2 px fotky na CSS pixel –
//    na malém výřezu (šířka × 40 % výšky) rozdíl proti 3× není vidět.
//  • tablet (481–768 px): skutečná šířka, na hustém displeji velká verze.
//  • počítač: fotka přes půl okna – vždy velká verze. Při „50vw“ by Chrome
//    na Evině monitoru (1536 px se škálováním 125 %) vzal náhled, kterému
//    ~20 % rozlišení chybí, a u tabletu na výšku je fotka podle výšky okna
//    ještě širší než půlka.
const PHOTO = '/photos/vanoce/01';
const PHOTO_WIDTHS = { thumb: 800, full: 1333 };
const PHOTO_SIZES = `(max-width: 480px) 266px, (max-width: 768px) 100vw, ${PHOTO_WIDTHS.full}px`;

const INSTAGRAM_URL = 'https://www.instagram.com/hrachovenachova/';

// Po návratu na záložku se obsazenost načte znovu, ale nejvýš jednou za 30 s
const REFRESH_MIN_INTERVAL = 30 * 1000;

// Server rezervaci uloží hned, ale odpoví až po odeslání e-mailů – když to
// trvá déle, nenecháme tlačítko točit, dokud spojení neutne proxy
const SUBMIT_TIMEOUT = 30 * 1000;

const GENERIC_ERROR =
  'Rezervaci se nepodařilo odeslat. Zkuste to prosím znovu, nebo mi napište přes kontaktní formulář.';

// Výpadek spojení, vypršení času nebo chyba serveru (5xx): rezervace se mohla
// uložit, jen se o tom nedozvíme – rovnou „zkuste znovu“ by vedlo k druhé
// rezervaci a hlášce, že termín zabral někdo jiný
const UNCERTAIN_ERROR =
  'Nepodařilo se ověřit, jestli rezervace prošla – možná se uložila. Než ji pošlete znovu, ' +
  'podívejte se prosím do e-mailu (i do spamu), jestli vám přišlo potvrzení. Když nepřijde, ' +
  'vyberte si čas a zkuste to znovu, případně mi napište přes kontaktní formulář.';

// Termín po nejisté rezervaci je obsazený – nejspíš právě jí
const OWN_SLOT_TAKEN_ERROR =
  'Tento termín už je zarezervovaný – nejspíš vaší předchozí rezervací. Podívejte se prosím ' +
  'do e-mailu (i do spamu); kdyby potvrzení nepřišlo, napište mi přes kontaktní formulář.';

// ─── Kontrakt s backendem (GET / POST /api/reservations) ─────────────────────

type SlotState = 'free' | 'taken' | 'closed';

/** Proč rezervace (ne)běží – starší backend posílá jen bookingOpen */
type BookingState = 'open' | 'not-started' | 'ended' | 'unavailable';

interface ApiSlot {
  id: string;
  /** „09:00“ – s úvodní nulou, do textů se převádí přes formatTime */
  time: string;
  state: SlotState;
}

interface ApiDay {
  date: string;
  /** „sobota 7. 11.“ */
  label: string;
  slots: ApiSlot[];
}

interface ApiEvent {
  id: string;
  title: string;
  bookingOpen: boolean;
  bookingState?: BookingState;
  /** Celková cena focení (starší backend ji neposílá) */
  priceTotal?: number;
  depositAmount: number;
  currency: string;
  /** Kde se fotí (starší backend neposílá) */
  location?: string;
  /** Co je v ceně (starší backend neposílá) */
  included?: string;
  depositDueDays: number;
  slotMinutes: number;
  /** Podmínky rezervace – stejný text jde i do potvrzovacího e-mailu */
  terms: string[];
  /** Kolik aktivních rezervací smí mít jeden e-mail / telefon */
  maxActivePerContact: number;
}

interface SlotsResponse {
  event: ApiEvent;
  days: ApiDay[];
}

interface ReservationResult {
  success: true;
  reservation: { slotId: string; date: string; time: string; label: string };
  payment: {
    account: string;
    iban: string;
    amount: number;
    currency: string;
    vs: string;
    message: string;
    dueDate: string;
    dueDateLabel: string;
    qrDataUrl: string;
  };
  emailSent: boolean;
}

interface ApiErrorBody {
  error?: string;
  code?: string;
  fields?: { field: string; message: string }[];
}

const FIELD_NAMES = ['name', 'email', 'phone', 'note', 'consent'] as const;
type FieldName = (typeof FIELD_NAMES)[number];
type FieldErrors = Partial<Record<FieldName, string>>;

const isFieldName = (field: string): field is FieldName =>
  (FIELD_NAMES as readonly string[]).indexOf(field) !== -1;

const EMPTY_FORM = {
  name: '',
  email: '',
  phone: '',
  note: '',
  consent: false,
  // Past na roboty – člověk pole nevidí, takže zůstane prázdné
  website: '',
};

// ─── Pomocné funkce ─────────────────────────────────────────────────────────

function isSlotsResponse(body: unknown): body is SlotsResponse {
  const b = body as SlotsResponse | null;
  return (
    !!b &&
    typeof b === 'object' &&
    !!b.event &&
    Array.isArray(b.days) &&
    b.days.every((day) => !!day && Array.isArray(day.slots))
  );
}

function isReservationResult(body: unknown): body is ReservationResult {
  const b = body as ReservationResult | null;
  return !!b && typeof b === 'object' && !!b.reservation && !!b.payment;
}

/** Podmínky z API – starší backend je neposílá, pak se seznam nezobrazí */
function eventTerms(event: ApiEvent): string[] {
  return Array.isArray(event.terms)
    ? event.terms.filter((term) => typeof term === 'string' && term.trim() !== '')
    : [];
}

/** Záloha, kdyby server u CONTACT_LIMIT hlášku neposlal */
function contactLimitMessage(limit: number | undefined): string {
  const count =
    typeof limit === 'number' && limit > 0
      ? `nejvýš ${limit} ${limit === 1 ? 'termín' : limit <= 4 ? 'termíny' : 'termínů'}`
      : 'jen omezený počet termínů';
  return (
    `Na jeden e-mail nebo telefon jde rezervovat ${count}. ` +
    'Pokud potřebujete víc, napište mi prosím.'
  );
}

/** „09:30“ → „9:30“ – v textech bez úvodní nuly */
const formatTime = (time: string) => time.replace(/^0(\d)/, '$1');

/** „sobota 7. 11.“ → „Sobota 7. 11.“ */
const capitalize = (text: string) => text.charAt(0).toUpperCase() + text.slice(1);

/** „sobota 7. 11. 2026 v 9:30“ */
function slotLabel(day: ApiDay, slot: ApiSlot): string {
  const year = day.date.slice(0, 4);
  const dayLabel = day.label.indexOf(year) !== -1 ? day.label : `${day.label} ${year}`;
  return `${dayLabel} v ${formatTime(slot.time)}`;
}

/** „15:30“ + 30 min → „16:00“ */
function addMinutes(time: string, minutes: number): string {
  const [hours, mins] = time.split(':').map(Number);
  const total = hours * 60 + mins + minutes;
  return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, '0')}`;
}

/** Popisek pod dnem: „12 volných“ / „obsazeno“ */
function freeCountLabel(day: ApiDay): string {
  const free = day.slots.filter((slot) => slot.state === 'free').length;
  if (free === 0) {
    return day.slots.every((slot) => slot.state === 'closed') ? 'uzavřeno' : 'obsazeno';
  }
  if (free === 1) return '1 volný';
  if (free <= 4) return `${free} volné`;
  return `${free} volných`;
}

function formatAmount(amount: number, currency: string): string {
  return currency === 'CZK' ? formatCzk(amount) : `${amount} ${currency}`;
}

/** „2026-11-08“ → „2026-11-07“ (počítá se v UTC, ať do toho nemluví časové pásmo) */
function previousDate(date: string): string {
  const [year, month, day] = date.split('-').map(Number);
  return new Date(Date.UTC(year, month - 1, day - 1)).toISOString().slice(0, 10);
}

function findSlot(data: SlotsResponse, slotId: string): { day: ApiDay; slot: ApiSlot } | null {
  for (const day of data.days) {
    const slot = day.slots.find((s) => s.id === slotId);
    if (slot) return { day, slot };
  }
  return null;
}

const prefersReducedMotion = () =>
  window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;

// ─── Sekce ──────────────────────────────────────────────────────────────────

const ChristmasBooking: React.FC = () => {
  const { colorMode } = useColorMode();
  const isDark = colorMode === 'dark';

  const [status, setStatus] = useState<'loading' | 'ready' | 'error'>('loading');
  const [data, setData] = useState<SlotsResponse | null>(null);
  const [activeDate, setActiveDate] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  // Formulář se odemkne prvním výběrem času a pak už zůstane odemčený –
  // i když se výběr zruší (termín mezitím zabral někdo jiný), vyplněná
  // pole nesmí zmizet.
  const [formUnlocked, setFormUnlocked] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [result, setResult] = useState<ReservationResult | null>(null);
  // Po chybách validace ze serveru se přesuneme na první chybné pole – hlášky
  // jsou u polí, kdežto tlačítko (a návštěvník) je o kus níž. Nový objekt =
  // nový požadavek, i když jde znovu o stejné pole.
  const [focusRequest, setFocusRequest] = useState<{ field: FieldName } | null>(null);

  const abortRef = useRef<AbortController | null>(null);
  const lastLoadRef = useRef(0);
  // Ref, ne state – dvojklik musí narazit hned, ne až po překreslení
  const submittingRef = useRef(false);
  // Termín z rezervace, o které nevíme, jestli prošla (viz UNCERTAIN_ERROR)
  const uncertainSlotRef = useRef<string | null>(null);
  const bookingRef = useRef<HTMLDivElement>(null);
  const successRef = useRef<HTMLDivElement>(null);

  // silent = obnovení na pozadí: nic neschovávat, při chybě nechat poslední stav
  const loadSlots = useCallback(async (silent = false) => {
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    lastLoadRef.current = Date.now();
    if (!silent) setStatus('loading');

    try {
      const response = await fetch(`${API_URL}/api/reservations`, {
        signal: controller.signal,
        cache: 'no-store',
      });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const body: unknown = await response.json();
      if (!isSlotsResponse(body)) throw new Error('Neočekávaná odpověď serveru');
      setData(body);
      setStatus('ready');
    } catch {
      if (controller.signal.aborted) return;
      // Obnovení na pozadí nechá poslední známý stav – ale jen pokud nějaký
      // je; kdyby přerušilo první načítání, sekce by jinak visela v „načítám“
      setStatus((prev) => (silent && prev === 'ready' ? prev : 'error'));
    }
  }, []);

  useEffect(() => {
    loadSlots();
    return () => abortRef.current?.abort();
  }, [loadSlots]);

  // Kdo má stránku otevřenou dlouho, ať po návratu nevybírá z neaktuálních časů
  useEffect(() => {
    const handleVisibility = () => {
      if (document.visibilityState !== 'visible') return;
      if (Date.now() - lastLoadRef.current < REFRESH_MIN_INTERVAL) return;
      loadSlots(true);
    };
    document.addEventListener('visibilitychange', handleVisibility);
    return () => document.removeEventListener('visibilitychange', handleVisibility);
  }, [loadSlots]);

  // Vybraný čas mezitím zabral někdo jiný (zjištěno při obnovení) – zrušit výběr.
  // Když rezervace neběží, nic se neruší: samé „uzavřeno“ backend posílá
  // i při výpadku úložiště, formulář se tehdy stejně neukazuje a hláška
  // o obsazeném čase by po „Zkusit znovu“ visela u času, který je volný.
  useEffect(() => {
    if (!data || !data.event.bookingOpen || !selectedId || submittingRef.current) return;
    const found = findSlot(data, selectedId);
    if (!found || found.slot.state !== 'free') {
      setSelectedId(null);
      setFormError(
        found?.slot.state === 'taken' && selectedId === uncertainSlotRef.current
          ? OWN_SLOT_TAKEN_ERROR
          : 'Vybraný čas už bohužel není volný – vyberte si prosím jiný.'
      );
    }
  }, [data, selectedId]);

  useEffect(() => {
    if (!focusRequest) return;
    const field = bookingRef.current?.querySelector<HTMLElement>(
      `[name="${focusRequest.field}"]`
    );
    if (!field) return;
    field.scrollIntoView({
      behavior: prefersReducedMotion() ? 'auto' : 'smooth',
      block: 'center',
    });
    field.focus({ preventScroll: true });
  }, [focusRequest]);

  // Potvrzení s platebními údaji musí být hned vidět
  useEffect(() => {
    if (!result) return;
    successRef.current?.scrollIntoView({
      behavior: prefersReducedMotion() ? 'auto' : 'smooth',
      block: 'start',
    });
    successRef.current?.focus({ preventScroll: true });
  }, [result]);

  const days = useMemo(() => data?.days ?? [], [data]);
  const event = data?.event ?? null;

  // Bez ručního výběru se ukáže první den, kde ještě je volno
  const activeDay = useMemo(
    () =>
      days.find((day) => day.date === activeDate) ??
      days.find((day) => day.slots.some((slot) => slot.state === 'free')) ??
      days[0],
    [days, activeDate]
  );

  const selected = data && selectedId ? findSlot(data, selectedId) : null;

  const handleSelect = (day: ApiDay, slot: ApiSlot) => {
    if (slot.state !== 'free') return;
    // Den se zafixuje – jinak by obnovení na pozadí mohlo přepnout mřížku
    // na jiný „první den s volnem“ a vybraný čas by zmizel z očí
    setActiveDate(day.date);
    setSelectedId(slot.id);
    setFormUnlocked(true);
    setFormError(null);
  };

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    if (isFieldName(name)) {
      setFieldErrors((prev) => (prev[name] ? { ...prev, [name]: undefined } : prev));
    }
  };

  const handleConsentChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { checked } = e.target;
    setForm((prev) => ({ ...prev, consent: checked }));
    setFieldErrors((prev) => (prev.consent ? { ...prev, consent: undefined } : prev));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (submittingRef.current || !selected) return;

    submittingRef.current = true;
    setIsSubmitting(true);
    setFormError(null);
    setFieldErrors({});

    const slotId = selected.slot.id;
    const controller = new AbortController();
    const timeout = window.setTimeout(() => controller.abort(), SUBMIT_TIMEOUT);

    // Nevíme, jestli rezervace prošla: výběr zrušit (ať to nejde jen znovu
    // odklepnout) a obsazenost načíst znovu – uložený termín se ukáže jako obsazený
    const handleUncertain = () => {
      uncertainSlotRef.current = slotId;
      setSelectedId(null);
      setFormError(UNCERTAIN_ERROR);
      loadSlots(true);
    };

    try {
      const response = await fetch(`${API_URL}/api/reservations`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        signal: controller.signal,
        // Jen to, co backend čeká – nic navíc
        body: JSON.stringify({
          slotId,
          name: form.name,
          email: form.email,
          phone: form.phone,
          note: form.note,
          consent: form.consent,
          website: form.website,
        }),
      });
      const body: ApiErrorBody & Partial<ReservationResult> = await response
        .json()
        .catch(() => ({}));

      if (response.ok && isReservationResult(body)) {
        setResult(body);
        setSelectedId(null);
        setFormUnlocked(false);
        setForm(EMPTY_FORM);
        loadSlots(true);
        return;
      }

      if (response.ok) {
        // Server rezervaci přijal, jen odpověď nedává smysl – znovu odesílat
        // nemá cenu, termín už je zabraný
        uncertainSlotRef.current = slotId;
        setSelectedId(null);
        setFormError(
          'Rezervace se nejspíš uložila, ale nepodařilo se zobrazit potvrzení. ' +
            'Podívejte se prosím do e-mailu, případně mi napište přes kontaktní formulář.'
        );
        loadSlots(true);
        return;
      }

      // 503 BOOKING_CLOSED backend posílá, jen když nic neuložil; jiná chyba
      // serveru nebo proxy (500, 502, 504…) mohla přijít až po uložení
      if (response.status >= 500 && body.code !== 'BOOKING_CLOSED') {
        handleUncertain();
        return;
      }

      // Hlášky serveru se ukazují jen u známých chyb rezervace (s kódem,
      // validace, limity) – u neznámé chyby (třeba z proxy) nevíme, co v ní je
      const serverMessage = typeof body.error === 'string' && body.error ? body.error : null;

      // Na tenhle e-mail / telefon je už rezervováno, kolik backend dovolí.
      // Termín sám je volný – výběr i vyplněná pole zůstávají a obsazenost
      // se nenačítá znovu (nic se nezměnilo); hláška radí napsat Evě
      if (response.status === 409 && body.code === 'CONTACT_LIMIT') {
        setFormError(serverMessage ?? contactLimitMessage(data?.event.maxActivePerContact));
        return;
      }

      switch (response.status) {
        case 409: // SLOT_TAKEN (CONTACT_LIMIT výše)
        case 410: // SLOT_CLOSED
          // Vyplněné údaje zůstávají, jen se vybere jiný čas
          setSelectedId(null);
          setFormError(
            response.status === 409 && slotId === uncertainSlotRef.current
              ? OWN_SLOT_TAKEN_ERROR
              : serverMessage ?? 'Tento čas už bohužel není volný – vyberte si prosím jiný.'
          );
          loadSlots(true);
          break;

        case 400:
          if (body.code === 'INVALID_SLOT') {
            setSelectedId(null);
            setFormError(serverMessage ?? 'Tento termín neexistuje – vyberte si prosím jiný.');
            loadSlots(true);
          } else {
            // BAD_REQUEST (nečitelný požadavek) i 400 bez kódu (proxy) jsou technické
            // chyby – návštěvník dostane obecnou hlášku s odkazem na kontakt
            setFormError(
              (body.code && body.code !== 'BAD_REQUEST' && serverMessage) || GENERIC_ERROR
            );
          }
          break;

        case 422: {
          const errors: FieldErrors = {};
          const other: string[] = [];
          (Array.isArray(body.fields) ? body.fields : []).forEach(({ field, message }) => {
            if (isFieldName(field)) {
              if (!errors[field]) errors[field] = message;
            } else {
              other.push(message);
            }
          });
          setFieldErrors(errors);
          const firstInvalid = FIELD_NAMES.find((field) => errors[field]);
          if (firstInvalid) setFocusRequest({ field: firstInvalid });
          // Souhrn i u tlačítka – hlášky u polí bývají mimo obrazovku
          setFormError(
            [...other, firstInvalid ? 'Zkontrolujte prosím zvýrazněná pole.' : '']
              .filter(Boolean)
              .join(' ') ||
              serverMessage ||
              'Zkontrolujte prosím vyplněné údaje.'
          );
          break;
        }

        case 429:
          // Limit rezervací (TOO_MANY_BOOKINGS) i obecný limit požadavků
          // (RATE_LIMITED) posílají českou hlášku; jinou 429 (bez kódu, třeba
          // z proxy) nahradí česká záloha
          setFormError(
            ((body.code === 'TOO_MANY_BOOKINGS' || body.code === 'RATE_LIMITED') &&
              serverMessage) ||
              'Požadavků bylo z vašeho připojení příliš mnoho. Zkuste to prosím později.'
          );
          break;

        case 503: // BOOKING_CLOSED (ostatní 5xx řeší handleUncertain výše)
          // Sekce hned přejde do stavu „spustím brzy“; nové načtení pak ukáže,
          // jestli rezervace opravdu nejsou spuštěné, nebo jde o výpadek úložiště
          setData((prev) =>
            prev
              ? { ...prev, event: { ...prev.event, bookingOpen: false, bookingState: undefined } }
              : prev
          );
          loadSlots(true);
          break;

        default:
          setFormError(GENERIC_ERROR);
      }
    } catch {
      // Síťová chyba nebo vypršený čas – požadavek mohl na server dorazit
      handleUncertain();
    } finally {
      window.clearTimeout(timeout);
      submittingRef.current = false;
      setIsSubmitting(false);
    }
  };

  const handleBookAnother = () => {
    setResult(null);
    setSelectedId(null);
    setFormUnlocked(false);
    setForm(EMPTY_FORM);
    setFieldErrors({});
    setFormError(null);
    loadSlots(true);
    bookingRef.current?.scrollIntoView({
      behavior: prefersReducedMotion() ? 'auto' : 'smooth',
      block: 'start',
    });
  };

  // ─── Barvy polí formuláře – stejně jako v kontaktu ────────────────────────
  const labelColor = isDark ? 'gray.300' : 'black';
  const fieldColors = {
    bg: isDark ? 'gray.700' : 'white',
    color: isDark ? 'white' : 'black',
    borderColor: isDark ? 'gray.600' : '#ddd',
  };
  const subTextColor = isDark ? 'gray.300' : '#555';

  // ─── Řádky s informacemi – cena, místo a obsah chodí z backendu ───────────
  const facts: { label: string; value: string }[] = [];
  const firstDaySlots = days[0]?.slots ?? [];
  const timeRange =
    event && firstDaySlots.length > 0
      ? `${formatTime(firstDaySlots[0].time)}–${addMinutes(
          firstDaySlots[firstDaySlots.length - 1].time,
          event.slotMinutes
        )}`
      : null;
  facts.push({
    label: 'Termín',
    value: timeRange ? `${CHRISTMAS_DATES_SHORT}, ${timeRange}` : CHRISTMAS_DATES_SHORT,
  });
  // Délku termínu (30 min) schválně neukazujeme – fotí se 20 minut (řádek
  // „V ceně“) a zbytek je rezerva na přípravu, dvě čísla by mátla.
  if (event?.location) facts.push({ label: 'Místo', value: event.location });
  if (event?.included) facts.push({ label: 'V ceně', value: event.included });
  if (event?.priceTotal) {
    facts.push({ label: 'Cena', value: formatAmount(event.priceTotal, event.currency) });
  }
  if (event) {
    const balance = event.priceTotal ? event.priceTotal - event.depositAmount : 0;
    facts.push({
      label: 'Záloha',
      value:
        balance > 0
          ? `${formatAmount(event.depositAmount, event.currency)} předem, ` +
            `doplatek ${formatAmount(balance, event.currency)} na místě`
          : formatAmount(event.depositAmount, event.currency),
    });
  }

  // ─── Jednotlivé stavy rezervace ───────────────────────────────────────────

  const renderSkeleton = () => (
    <Box className="xmas-skeleton" aria-busy="true">
      <span className="xmas-visually-hidden" role="status">
        Načítám volné termíny…
      </span>
      <Box className="xmas-days" aria-hidden="true">
        <span className="xmas-skeleton-block xmas-skeleton-day" />
        <span className="xmas-skeleton-block xmas-skeleton-day" />
      </Box>
      <Box className="xmas-times" aria-hidden="true">
        {Array.from({ length: 14 }, (_, i) => (
          <span key={i} className="xmas-skeleton-block xmas-skeleton-time" />
        ))}
      </Box>
    </Box>
  );

  const contactLink = (text: string) => (
    <Link href="#contact" className="xmas-inline-link">
      {text}
    </Link>
  );

  const renderMessage = (content: React.ReactNode, isAlert = false) => (
    <Box className="xmas-message" role={isAlert ? 'alert' : undefined}>
      <Text className="xmas-message-text">{content}</Text>
    </Box>
  );

  const renderError = () => (
    <Box className="xmas-message" role="alert">
      <Text className="xmas-message-text">
        Rezervační systém se teď nepodařilo načíst. Zkuste to prosím za chvíli, nebo mi napište
        přes {contactLink('kontaktní formulář')}.
      </Text>
      <button type="button" className="xmas-secondary-button" onClick={() => loadSlots()}>
        Zkusit znovu
      </button>
    </Box>
  );

  const renderSuccess = (res: ReservationResult) => {
    const { payment } = res;
    const amount = formatAmount(payment.amount, payment.currency);
    const rows: { label: string; value: string }[] = [
      { label: 'Číslo účtu', value: payment.account },
      { label: 'IBAN', value: payment.iban },
      { label: 'Částka', value: amount },
      { label: 'Variabilní symbol', value: payment.vs },
      { label: 'Zpráva pro příjemce', value: payment.message },
    ];

    return (
      <Box ref={successRef} tabIndex={-1} className="xmas-success">
        <Text as="h3" className="xmas-success-title">
          Děkuji, termín je pro vás předběžně rezervovaný
        </Text>
        <Text className="xmas-success-slot">{capitalize(res.reservation.label)}</Text>
        <Text className="xmas-success-text" color={subTextColor}>
          Rezervace bude potvrzená po připsání zálohy {amount} do {payment.dueDateLabel}.
        </Text>

        <Box className="xmas-payment">
          {payment.qrDataUrl && (
            <Box className="xmas-qr">
              <img src={payment.qrDataUrl} alt="QR platba zálohy" width={210} height={210} />
            </Box>
          )}
          <dl className="xmas-pay-table">
            {rows.map((row) => (
              <div key={row.label} className="xmas-pay-row">
                <dt>{row.label}</dt>
                <dd>{row.value}</dd>
              </div>
            ))}
          </dl>
        </Box>

        {res.emailSent ? (
          <Text className="xmas-success-text" color={subTextColor}>
            Stejné údaje jsem vám poslala e-mailem.
          </Text>
        ) : (
          <Text className="xmas-notice" role="alert">
            Potvrzovací e-mail se nepodařilo odeslat – platební údaje si prosím uložte (třeba
            snímkem obrazovky). Ozvu se vám.
          </Text>
        )}

        <button type="button" className="xmas-secondary-button" onClick={handleBookAnother}>
          Rezervovat další termín
        </button>
      </Box>
    );
  };

  const renderPicker = () => (
    <>
      <Text as="h3" className="xmas-step">
        Vyberte den a čas
      </Text>

      <Box className="xmas-days" role="group" aria-label="Den focení">
        {days.map((day) => {
          const isActive = day.date === activeDay?.date;
          return (
            <button
              key={day.date}
              type="button"
              className={`xmas-day${isActive ? ' is-active' : ''}`}
              aria-pressed={isActive}
              onClick={() => setActiveDate(day.date)}
            >
              <span className="xmas-day-label">{capitalize(day.label)}</span>
              <span className="xmas-day-count">{freeCountLabel(day)}</span>
            </button>
          );
        })}
      </Box>

      {activeDay && (
        <Box className="xmas-times" role="group" aria-label={`Časy – ${activeDay.label}`}>
          {activeDay.slots.map((slot) => {
            const isSelected = slot.id === selectedId;
            const stateLabel =
              slot.state === 'taken' ? 'obsazeno' : slot.state === 'closed' ? 'uzavřeno' : undefined;
            return (
              <button
                key={slot.id}
                type="button"
                className={`xmas-time xmas-time--${slot.state}${isSelected ? ' is-selected' : ''}`}
                aria-pressed={isSelected}
                aria-label={stateLabel ? `${formatTime(slot.time)} – ${stateLabel}` : undefined}
                title={stateLabel}
                disabled={slot.state !== 'free'}
                onClick={() => handleSelect(activeDay, slot)}
              >
                {formatTime(slot.time)}
              </button>
            );
          })}
        </Box>
      )}

      <Box className="xmas-legend" aria-hidden="true">
        <span className="xmas-legend-item">
          <span className="xmas-legend-swatch" /> volno
        </span>
        <span className="xmas-legend-item">
          <span className="xmas-legend-swatch xmas-legend-swatch--taken" /> obsazeno
        </span>
        <span className="xmas-legend-item">
          <span className="xmas-legend-swatch xmas-legend-swatch--selected" /> vybráno
        </span>
        {activeDay?.slots.some((slot) => slot.state === 'closed') && (
          <span className="xmas-legend-item">
            <span className="xmas-legend-swatch xmas-legend-swatch--closed" /> uzavřeno
          </span>
        )}
      </Box>

      {/* Poznámka „pod čarou“ – jeden termín nejde sdílet víc rodinami */}
      <Text className="xmas-footnote">Jeden čas je vždy pro jednu rodinu.</Text>
    </>
  );

  const renderForm = (terms: string[]) => (
    <Box
      as="form"
      onSubmit={handleSubmit}
      className={`xmas-form${formUnlocked ? '' : ' is-locked'}`}
    >
      <Text as="h3" className="xmas-step">
        Vaše údaje
      </Text>
      <Text className="xmas-summary" aria-live="polite">
        {selected ? (
          <>
            Vybraný termín:{' '}
            <span className="xmas-summary-slot">{slotLabel(selected.day, selected.slot)}</span>
          </>
        ) : formUnlocked ? (
          'Vyberte si prosím čas v nabídce výše.'
        ) : (
          'Nejdřív si prosím vyberte čas, pak vyplníte pár údajů.'
        )}
      </Text>

      {/* Past na roboty – mimo obrazovku, čtečky i klávesnice ji přeskočí */}
      <Box className="xmas-hp" aria-hidden="true">
        <label>
          Web
          <input
            type="text"
            name="website"
            tabIndex={-1}
            autoComplete="off"
            value={form.website}
            onChange={handleInputChange}
          />
        </label>
      </Box>

      <VStack spacing={4} align="stretch">
        <FormControl isRequired isDisabled={!formUnlocked} isInvalid={!!fieldErrors.name}>
          <FormLabel className="form-label" color={labelColor}>
            Jméno a příjmení
          </FormLabel>
          <Input
            name="name"
            value={form.name}
            onChange={handleInputChange}
            autoComplete="name"
            minLength={2}
            maxLength={120}
            className="form-input"
            {...fieldColors}
          />
          <FormErrorMessage className="xmas-field-error">{fieldErrors.name}</FormErrorMessage>
        </FormControl>

        <FormControl isRequired isDisabled={!formUnlocked} isInvalid={!!fieldErrors.email}>
          <FormLabel className="form-label" color={labelColor}>
            E-mail
          </FormLabel>
          <Input
            type="email"
            name="email"
            value={form.email}
            onChange={handleInputChange}
            autoComplete="email"
            maxLength={320}
            className="form-input"
            {...fieldColors}
          />
          <FormErrorMessage className="xmas-field-error">{fieldErrors.email}</FormErrorMessage>
        </FormControl>

        <FormControl isRequired isDisabled={!formUnlocked} isInvalid={!!fieldErrors.phone}>
          <FormLabel className="form-label" color={labelColor}>
            Telefon
          </FormLabel>
          <Input
            type="tel"
            name="phone"
            value={form.phone}
            onChange={handleInputChange}
            autoComplete="tel"
            maxLength={30}
            className="form-input"
            {...fieldColors}
          />
          <FormErrorMessage className="xmas-field-error">{fieldErrors.phone}</FormErrorMessage>
        </FormControl>

        <FormControl isDisabled={!formUnlocked} isInvalid={!!fieldErrors.note}>
          <FormLabel className="form-label" color={labelColor}>
            Poznámka
          </FormLabel>
          <Textarea
            name="note"
            value={form.note}
            onChange={handleInputChange}
            placeholder="Kolik vás bude, děti, pejsek…"
            maxLength={1000}
            rows={3}
            className="form-textarea"
            {...fieldColors}
          />
          <FormErrorMessage className="xmas-field-error">{fieldErrors.note}</FormErrorMessage>
        </FormControl>

        {/* Podmínky posílá backend (jediný zdroj i pro e-mail); bez nich
            zůstává jen souhlas níž */}
        {terms.length > 0 && (
          <Box className="xmas-terms-box">
            <Text className="xmas-terms-title">Podmínky rezervace</Text>
            <ul className="xmas-terms">
              {terms.map((term, index) => (
                <li key={index}>{term}</li>
              ))}
            </ul>
          </Box>
        )}

        <FormControl isRequired isDisabled={!formUnlocked} isInvalid={!!fieldErrors.consent}>
          <Checkbox
            name="consent"
            isChecked={form.consent}
            onChange={handleConsentChange}
            className="xmas-consent"
          >
            Souhlasím s podmínkami rezervace a beru na vědomí{' '}
            <Link href="/privacy-policy" isExternal className="xmas-inline-link">
              zpracování osobních údajů
            </Link>
          </Checkbox>
          <FormErrorMessage className="xmas-field-error">{fieldErrors.consent}</FormErrorMessage>
        </FormControl>

        {formError && (
          <Text className="xmas-form-error" role="alert">
            {formError}
          </Text>
        )}

        <Button
          type="submit"
          isLoading={isSubmitting}
          loadingText="Rezervuji…"
          isDisabled={!selected}
          className="submit-button"
          size="lg"
        >
          Rezervovat termín
        </Button>
      </VStack>
    </Box>
  );

  const renderEnded = () =>
    renderMessage(
      <>
        Rezervace na vánoční focení jsou už uzavřené. Kdybyste se chtěli na něco zeptat,
        napište mi přes {contactLink('kontaktní formulář')}.
      </>
    );

  const renderBooking = () => {
    if (result) return renderSuccess(result);
    if (status === 'error') return renderError();
    if (status === 'loading' || !data || !event) return renderSkeleton();

    const allSlots = days.reduce<ApiSlot[]>((all, day) => all.concat(day.slots), []);

    if (!event.bookingOpen) {
      // Novější backend rovnou řekne proč – výpadek úložiště ≠ konec rezervací
      if (event.bookingState === 'unavailable') return renderError();
      if (event.bookingState === 'ended') return renderEnded();
      if (!event.bookingState && allSlots.every((slot) => slot.state === 'closed')) {
        // Samé „uzavřeno“ backend pošle po uzávěrce posledního termínu, ale
        // i když nemůže přečíst uložené rezervace (pak raději nic nenabízí).
        // Rozliší to datum: při uzávěrce 24 h předem (bookingClosesHoursBefore
        // na backendu) padne ta poslední na den před posledním dnem focení –
        // dřív jde o výpadek, ne o konec rezervací.
        // (Jen se starším backendem bez bookingState.)
        const lastDay = days.reduce((last, day) => (day.date > last ? day.date : last), '');
        if (!lastDay || pragueToday() < previousDate(lastDay)) return renderError();
        return renderEnded();
      }

      return renderMessage(
        <>
          Rezervace spustím už brzy – sledujte můj{' '}
          <Link href={INSTAGRAM_URL} isExternal className="xmas-inline-link">
            Instagram
          </Link>
          .
        </>
      );
    }

    if (!allSlots.some((slot) => slot.state === 'free')) {
      return renderMessage(
        <>
          Všechny termíny jsou obsazené. {contactLink('Napište mi')}, ráda vás zapíšu jako
          náhradníka.
        </>
      );
    }

    return (
      <>
        {renderPicker()}
        {renderForm(eventTerms(event))}
      </>
    );
  };

  return (
    <Box
      id={CHRISTMAS_SECTION_ID}
      as="section"
      className="xmas-container"
      aria-labelledby="vanoce-title"
      bg={isDark ? 'gray.900' : 'white'}
      color={isDark ? 'white' : 'black'}
    >
      <Box className="xmas-content">
        <Box className="xmas-media">
          <Box className="xmas-media-frame">
            <ThemePhoto
              base={PHOTO}
              alt="Zasněžený smrček s bílou mašlí v mlžném lese"
              sizes={PHOTO_SIZES}
              widths={PHOTO_WIDTHS}
              className="xmas-photo"
            />
          </Box>
        </Box>

        <Box className="xmas-body reveal">
          <Box className="xmas-inner">
            <Text as="h2" id="vanoce-title" className="xmas-title">
              {CHRISTMAS_TITLE}
            </Text>
            <Text className="xmas-subtitle" color={subTextColor}>
              {CHRISTMAS_SUBTITLE}
            </Text>
            <Text className="xmas-intro" color={subTextColor}>
              {CHRISTMAS_INTRO}
            </Text>

            <dl className="xmas-facts">
              {facts.map((fact) => (
                <div key={fact.label} className="xmas-fact">
                  <dt>{fact.label}</dt>
                  <dd>{fact.value}</dd>
                </div>
              ))}
            </dl>

            <Box ref={bookingRef} className="xmas-booking">
              {renderBooking()}
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default ChristmasBooking;
